/**
 * Hook for making authenticated API requests using Clerk's session token.
 */
import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Returns a fetch function that automatically includes the Clerk auth token.
 *
 * @example
 * const authFetch = useAuthenticatedFetch();
 * const data = await authFetch('/api/v2/users/me');
 */
export function useAuthenticatedFetch() {
  const { getToken } = useAuth();

  const authFetch = useCallback(
    async <T = unknown>(url: string, options: RequestInit = {}): Promise<T> => {
      const token = await getToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const headers = new Headers(options.headers);
      headers.set('Authorization', `Bearer ${token}`);
      if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(`${API_BASE}${url}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(error.error?.message || error.detail || `HTTP ${response.status}`);
      }

      return response.json();
    },
    [getToken]
  );

  return authFetch;
}
